import type {
  FishPricesByProduct,
  FishProductId,
  FishShopV2State,
  FishTicketsByProduct,
} from "@cloudnest/fish-shop-v2-engine";

export type FishShopReplayEvent =
  | {
      readonly eventType: "init";
      readonly userId: string;
      readonly payload: { readonly state: FishShopV2State };
    }
  | {
      readonly eventType: "SyncViewerState";
      readonly userId: string;
      readonly payload?: { readonly actionId?: string };
    }
  | {
      readonly eventType: "AllocateTickets";
      readonly userId: string;
      readonly payload: { readonly ticketsByProduct: FishTicketsByProduct };
    }
  | {
      readonly eventType: "SubmitPrices";
      readonly userId: string;
      readonly payload: { readonly prices: FishPricesByProduct };
    }
  | {
      readonly eventType: "UseSecret";
      readonly userId: string;
      readonly payload: { readonly productId: FishProductId };
    }
  | {
      readonly eventType: "UseCheck";
      readonly userId: string;
      readonly payload?: Readonly<Record<string, never>>;
    }
  | {
      readonly eventType: "ConfirmRoundResult";
      readonly userId: string;
      readonly payload?: Readonly<Record<string, never>>;
    }
  | {
      readonly eventType: "RestartGame";
      readonly userId: string;
      readonly payload?: Readonly<Record<string, never>>;
    };

export type FishShopReplayEventType = FishShopReplayEvent["eventType"];

export interface FishShopEventInput {
  readonly eventType: string;
  readonly userId?: string;
  readonly payload?: Record<string, unknown>;
}

export const FISH_SHOP_REPLAY_EVENT_TYPES = [
  "init",
  "SyncViewerState",
  "AllocateTickets",
  "SubmitPrices",
  "UseSecret",
  "UseCheck",
  "ConfirmRoundResult",
  "RestartGame",
] as const satisfies readonly FishShopReplayEventType[];

export function normalizeFishShopReplayEventType(
  eventType: string,
): FishShopReplayEventType {
  switch (eventType) {
    case "init":
    case "SyncViewerState":
    case "AllocateTickets":
    case "SubmitPrices":
    case "UseSecret":
    case "UseCheck":
    case "ConfirmRoundResult":
    case "RestartGame":
      return eventType;
    default:
      throw new Error(`Unsupported Fish Shop event type: ${eventType}`);
  }
}

function requireUserId(event: FishShopEventInput): string {
  if (typeof event.userId === "string" && event.userId.length > 0) {
    return event.userId;
  }
  throw new Error("Fish Shop replay event is missing userId");
}

export function toFishShopReplayEvent(
  event: FishShopEventInput,
): FishShopReplayEvent {
  const eventType = normalizeFishShopReplayEventType(event.eventType);
  const userId = requireUserId(event);
  const payload = event.payload ?? {};

  switch (eventType) {
    case "init":
      return {
        eventType,
        userId,
        payload: payload as { readonly state: FishShopV2State },
      };
    case "SyncViewerState":
      return {
        eventType,
        userId,
        payload: payload as { readonly actionId?: string },
      };
    case "AllocateTickets":
      return {
        eventType,
        userId,
        payload: payload as { readonly ticketsByProduct: FishTicketsByProduct },
      };
    case "SubmitPrices":
      return {
        eventType,
        userId,
        payload: payload as { readonly prices: FishPricesByProduct },
      };
    case "UseSecret":
      return {
        eventType,
        userId,
        payload: payload as { readonly productId: FishProductId },
      };
    case "UseCheck":
    case "ConfirmRoundResult":
    case "RestartGame":
      return { eventType, userId };
  }
}
